import React, { Fragment, useState } from 'react';
import Logo from '../../assets/logo-sm.svg';
import SubmitButton from '../../components/submitButton';
import { useAsync } from 'react-async';
import { Link } from 'react-router-dom';
import { Notification } from '../../components/notifications';
import { requestMfaReset } from '../../api/services/authService';

const RequestMfaReset = () => {
  const [emailAddress, setEmailAddress] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const isValidEmail = () => {
    const pattern = new RegExp(
      /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
    );
    return pattern.test(emailAddress);
  };

  const sendRequest = useAsync({
    deferFn: async () => {
      await requestMfaReset(emailAddress);
    },
    onResolve: () => {
      setSubmitted(true);
    },
    onReject: (error: unknown) => {
      setSendError(
        typeof error === 'string'
          ? error
          : 'Mail sending failed. Please try again later.'
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail()) {
      setHasValidationError(true);
      return;
    }
    setHasValidationError(false);
    sendRequest.run();
  };

  return (
    <div
      className='flex bg-ptsblue-normal flex-col w-full items-center justify-center'
      style={{ height: '100vh' }}
    >
      <div className='text-gray-900 w-full p-6 md:max-w-xl'>
        <form
          className='bg-white rounded-lg border shadow-lg p-10 mb-2'
          onSubmit={handleSubmit}
        >
          <img
            src={Logo}
            alt='Pediatric Therapeutic Services'
            className='object-contain object-center h-28 w-full mb-6'
          />

          <h2 className='text-xl font-semibold text-center mb-2'>
            Reset Two-Factor Authentication
          </h2>

          <p className='text-sm text-gray-600 text-center mb-3'>
            Enter the email address associated with your account and we'll send
            you a secure link to reset your two-factor authentication.
          </p>
          <div className='mb-6 p-3 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700'>
            <p className='font-semibold mb-1'>You may need to reset your MFA if:</p>
            <ul className='list-disc list-inside space-y-0.5'>
              <li>You lost or replaced your phone</li>
              <li>You deleted or reinstalled your authenticator app</li>
              <li>You switched to a new device</li>
              <li>Your verification codes are no longer being accepted</li>
              <li>You're locked out and unsure why</li>
            </ul>
          </div>

          {hasValidationError && (
            <Notification
              isSuccess={false}
              isOpen={hasValidationError}
              onClose={() => setHasValidationError(false)}
            >
              <Fragment>Please enter a valid email address.</Fragment>
            </Notification>
          )}

          {sendError && (
            <Notification
              isSuccess={false}
              isOpen={!!sendError}
              onClose={() => setSendError(null)}
            >
              <Fragment>{sendError}</Fragment>
            </Notification>
          )}

          {submitted ? (
            <div className='mb-4 p-4 bg-green-50 border border-green-200 rounded text-center'>
              <p className='text-sm text-green-800'>
                If your email address is registered, you will receive a reset
                link shortly. Please check your inbox (and spam folder).
              </p>
            </div>
          ) : (
            <div className='mb-6'>
              <label className='block mb-2' htmlFor='emailAddress'>
                Email Address
              </label>
              <input
                id='emailAddress'
                type='text'
                placeholder='your@email.com'
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div className='flex items-center justify-between'>
            {!submitted && (
              <SubmitButton spinning={sendRequest.isPending} text='Send Reset Link' />
            )}
            <Link to='/mfa' className='text-sm text-blue-600 hover:underline'>
              &lt; Back
            </Link>
          </div>
        </form>

        <p className='text-right text-gray-300 text-xs'>
          &copy;Copyright 2026 – Kelly Pediatric Therapy.
          <br />
          All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default RequestMfaReset;
